import React, { useMemo, useState, useContext } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  TablePagination,
  TableSortLabel,
  Paper,
  IconButton,
  Button,
  Tooltip,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { format } from 'date-fns';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  flexRender,
  createColumnHelper,
} from '@tanstack/react-table';
import CircularProgress from '@mui/material/CircularProgress';
import { UserContext } from '../../contexts/UserContext';
import JournalEntryModal from './JournalEntryModal';
import { buildJournalFormData } from '../../utils/buildJournalFormData';

import { useMsal } from '@azure/msal-react';
import { getAuthConfig, getApiBaseUrl } from '../../services/configService';

const columnHelper = createColumnHelper();

const InvoiceTable = ({ 
        data = [], 
        visitData, 
        statusData,
        journalIndex,
        updateCardStatusLocally, 
        refreshVisitDetails,
        onBulkJournal,      
      }) => {
  const { user } = useContext(UserContext);
  const [isSubmittingHold, setIsSubmittingHold] = useState(false);
  const [globalFilter, setGlobalFilter] = useState('');
  const [sorting, setSorting] = useState([]);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });
  const isOnHold = statusData?.current_status === 'Hold';

  const { instance, accounts } = useMsal();
  const authConfig = getAuthConfig();

  const normalizeDateKey = (date) => {
    if (!date) return null;
  
    let d;
  
    if (typeof date === 'string') {
      if (!date.includes('Z') && !date.includes('+')) {
        d = new Date(date.replace(' ', 'T') + 'Z');
      } else {
        d = new Date(date);
      }
    } else {
      d = new Date(date);
    }
  
    return d.toISOString().slice(0, 19);
  };  

  const getApiAccessToken = async () => {
    if (!accounts.length) return null;
  
    try {
      const res = await instance.acquireTokenSilent({
        account: accounts[0],
        scopes: [`api://${authConfig.clientId}/access_as_user`],
      });
      return res.accessToken;
    } catch {
      const res = await instance.acquireTokenPopup({
        scopes: [`api://${authConfig.clientId}/access_as_user`],
      });
      return res.accessToken;
    }
  };
  
  const postCardStatus = async (status) => {
    if (!visitData?.unique_id || !status) {
      console.error('Missing unique_id or status — cannot update card status');
      return;
    }
  
    const token = await getApiAccessToken();
    if (!token) {
      console.error('Unable to acquire API token');
      return;
    }
  
    const url = new URL(`${getApiBaseUrl()}/card-status`);
    url.searchParams.set('unique_id', visitData.unique_id);
    url.searchParams.set('status', status);
  
    const res = await fetch(url.toString(), {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  
    if (!res.ok) {
      const msg = await res.text();
      throw new Error(msg || 'Failed to update card status');
    }
  };

  const [openJournal, setOpenJournal] = useState(false);
  const [journalState, setJournalState] = useState(null);

  const openInvoiceJournal = (row) => {
    const built = buildJournalFormData({
      journalType: 'invoice',
      visit: visitData,
      invoiceLine: row,
      currentUser: user,
    });

    setJournalState(built);
    setOpenJournal(true);
  };

  const openBlankJournal = () => {
    const built = buildJournalFormData({
      journalType: 'blank',
      visit: visitData,
      currentUser: user,
    });

    setJournalState(built);
    setOpenJournal(true);
  };

  const closeJournal = () => {
    setOpenJournal(false);
    setJournalState(null);
  };

  const hasInvoiceJournal = (row) => {
    if (!row?.pims_id || !row?.completed_date) return false;
  
    const key = [
      visitData.unique_id,
      row.pims_id,
      normalizeDateKey(row.completed_date),
    ].join('|');
  
    return journalIndex?.byInvoiceKey?.has(key);
  };

  const columns = useMemo(
    () => [
      columnHelper.accessor('completed_date', {
        header: 'Date',
        cell: info => {
          const raw = info.getValue();
          if (!raw) return '';
          try {
            return format(new Date(raw), 'MM/dd/yy hh:mm a');
          } catch {
            return raw;
          }
        },
      }),
      columnHelper.accessor('label', { header: 'Label' }),
      columnHelper.accessor('amount', {
        header: 'Amount',
        cell: info => `$${Number(info.getValue() || 0).toFixed(2)}`,
      }),
      columnHelper.accessor('quantity', { header: 'Qty' }),
      columnHelper.accessor('pims_id', { header: 'PIMS ID' }),
      columnHelper.accessor('user', { header: 'User' }),
      columnHelper.accessor('is_voided', {
        header: 'Void?',
        cell: info => (info.getValue() ? 'Yes' : 'No'),
      }),
      columnHelper.accessor('from_pvl', {
        header: 'PVL',
        cell: info => (info.getValue() ? '✓' : ''),
      }),
      columnHelper.accessor('from_itp', {
        header: 'ITP',
        cell: info => (info.getValue() ? '✓' : ''),
      }),
      columnHelper.display({
        id: 'actions',
        header: '',
        cell: ({ row }) => {
          const { from_itp, from_pvl, is_voided } = row.original;
          const shouldShowEdit = (from_itp && !from_pvl) || is_voided;
          if (!shouldShowEdit) return null;

          const journalExists = hasInvoiceJournal(row.original);
        
          return (
            <Tooltip
              title={journalExists ? 'Journal already exists' : 'Journal Entry'}
            >
              <IconButton
                size="small"
                onClick={
                  journalExists
                    ? undefined
                    : (e) => {
                        e.stopPropagation();
                        openInvoiceJournal(row.original);
                      }
                }
                sx={{
                  pointerEvents: journalExists ? 'none' : 'auto',
                  cursor: journalExists ? 'default' : 'pointer',
                  padding: '2px',
                  width: '24px',
                  height: '24px',
                  border: '1px solid #ccc',
                  borderRadius: '4px',
                  backgroundColor: '#f5f5f5',
                  '&:hover': {
                    backgroundColor: '#e5e7eb',
                  },
                }}
              >
                {journalExists ? (
                  <CheckCircleIcon
                    fontSize="small"
                    className="icon-completed"
                  />
                ) : (
                  <EditIcon sx={{ fontSize: '16px' }} />
                )}
              </IconButton>
            </Tooltip>
          );
        },
      }),
    ],
    [user, visitData.unique_id, journalIndex]
  );

  const table = useReactTable({
    data,
    columns,
    state: { globalFilter, sorting, pagination },
    onGlobalFilterChange: setGlobalFilter,
    onSortingChange: setSorting,
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });
  
  return (
    <Box sx={{ width: '100%', marginTop: '1rem' }}>
      {/* Toolbar */}
      <Box
        className="invoice-toolbar"
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.75rem',
          mb: 2,
        }}
      >
        <TextField
          label="Search All"
          fullWidth
          value={globalFilter ?? ''}
          onChange={e => setGlobalFilter(e.target.value)}
        />

        <Box sx={{ display: 'flex', gap: '0.5rem' }}>        
          <Button
            variant="contained"
            onClick={onBulkJournal}
            startIcon={<PlaylistAddCheckIcon />}
            sx={bulkButtonStyles}
          >
            Bulk Journal
          </Button>

          <Button
            variant="contained"
            onClick={openBlankJournal}
            startIcon={<NoteAddIcon />}
            sx={bulkButtonStyles}
          >
            Blank Journal
          </Button>

          <Button
            variant="outlined"
            disabled={isSubmittingHold}
            startIcon={
              isSubmittingHold ? (
                <CircularProgress size={16} thickness={5} sx={{ color: isOnHold ? '#92400e' : '#fff' }} />
              ) : (
                <PauseCircleOutlineIcon />
              )
            }
            sx={{
              ...bulkButtonStyles,
              backgroundColor: isOnHold ? '#fef3c7' : '#3B4C66',
              color: isOnHold ? '#92400e' : 'white',
              border: isOnHold ? '1px solid #fcd34d' : 'none',
              opacity: isSubmittingHold ? 0.85 : 1,
              '&:hover': {
                backgroundColor: isOnHold ? '#fde68a' : '#2A3647',
              },
            }}
            onClick={async () => {
              try {
                setIsSubmittingHold(true);
                await postCardStatus('Hold');
                updateCardStatusLocally('Hold');
                await refreshVisitDetails({ silent: true });
              } catch (e) {
                console.error('Failed to set card status:', e.message);
              } finally {
                setIsSubmittingHold(false);
              }
            }}
          >
            {isSubmittingHold
              ? 'Saving...'
              : isOnHold
                ? 'On Hold'
                : 'Hold'}
          </Button>
        </Box>
      </Box>

      <TableContainer component={Paper} sx={{ maxHeight: 600 }}>
        <Table stickyHeader>
          <TableHead>
            {table.getHeaderGroups().map(headerGroup => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map(header => (
                  <TableCell key={header.id}>
                    {header.isPlaceholder ? null : (
                      <TableSortLabel
                        active={!!header.column.getIsSorted()}
                        direction={
                          header.column.getIsSorted() === 'desc'
                            ? 'desc'
                            : 'asc'
                        }
                        onClick={header.column.getToggleSortingHandler()}
                      >
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                      </TableSortLabel>
                    )}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableHead>

          <TableBody>
            {table.getRowModel().rows.map(row => (
              <TableRow key={row.id}>
                {row.getVisibleCells().map(cell => (
                  <TableCell 
                    key={cell.id}
                    className={cell.column.id === 'actions' ? 'no-row-hover' : undefined}
                  >
                    {flexRender(
                      cell.column.columnDef.cell,
                      cell.getContext()
                    )}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      <TablePagination
        component="div"
        count={table.getFilteredRowModel().rows.length}
        page={pagination.pageIndex}
        rowsPerPage={pagination.pageSize}
        onPageChange={(_, pageIndex) =>
          setPagination(prev => ({ ...prev, pageIndex }))
        }
        onRowsPerPageChange={e =>
          setPagination(prev => ({
            ...prev,
            pageSize: Number(e.target.value),
          }))
        }
        rowsPerPageOptions={[10, 25, 50]}
      />

      {/* Journal Modal */}
      <JournalEntryModal
        open={openJournal}
        onClose={closeJournal}
        onSuccess={closeJournal}
        journal={journalState}
        refreshVisitDetails={refreshVisitDetails}     
      />
    </Box>
  );
};

const bulkButtonStyles = {
  width: 175,
  height: 55,
  backgroundColor: '#3B4C66',
  color: 'white',
  fontFamily: 'Poppins, sans-serif',
  fontWeight: 600,
  textTransform: 'none',
  borderRadius: '6px',
  boxShadow: 'none',
  padding: '0 14px',
  display: 'flex',
  justifyContent: 'flex-start',
  textAlign: 'left',
  lineHeight: 1.2,
  whiteSpace: 'normal',
  '&:hover': {
    backgroundColor: '#2A3647',
  },
  '& .MuiButton-startIcon': {
    marginRight: '10px',
  },
};

export default InvoiceTable;